// src/components/resources/ResourceGrid.tsx

import type { ComponentProps } from 'react'
import * as motion from 'motion/react-client'
import type { ResourceInstance } from '~/lib/resources/types/shared/base'
import { type ResourceType, type ToolType } from '~/lib/resources/types/shared/enums'
import { ResourceCard } from '~/components/resources/ResourceCard'
import { cn } from '~/lib/utils'

interface ResourceGridProps {
  resources: ResourceInstance[]
  type?: 'default' | 'featured' | 'compact'
  category?: ResourceType | ToolType
  className?: string
  /**
   * Optional per-card feature overrides forwarded to every `ResourceCard`
   * (e.g. `{ showCategory: false }`). Additive and optional — omitting it
   * leaves the card's preset behaviour unchanged.
   */
  cardFeatures?: ComponentProps<typeof ResourceCard>['features']
  /** Optional per-card theme overrides (e.g. `{ imageAspectRatio: '16/9' }`). */
  cardTheme?: ComponentProps<typeof ResourceCard>['theme']
}

export default function ResourceGrid({
  resources,
  type = 'default',
  className,
  cardFeatures,
  cardTheme,
}: ResourceGridProps): JSX.Element {
  const gridConfig = {
    default: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8',
    featured: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8',
    compact: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start sm:gap-6'
  }

  return (
    <div className={cn("grid", gridConfig[type], className)}>
      {resources.map((resource, index) => (
        <motion.div
          key={resource.id}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          // `once: true` so a card never re-fades when it re-enters the
          // viewport on scroll-back. The positive bottom root-margin extends
          // the detection area ~200px below the fold so each card begins its
          // reveal just before it scrolls into view — no blank gap while you
          // wait for it to populate. The previous config had no viewport prop
          // (default `once: false`), which re-ran the fade on every scroll.
          viewport={{ once: true, margin: '0px 0px 200px 0px' }}
          // Cap the stagger so the grid never takes >~0.3s to fully reveal.
          // The old `index * 0.1` stacked to 2.4s+ across ~24 cards, which
          // is what made the page feel like it was slowly populating on
          // scroll. Stagger only within the first row, then reveal together.
          transition={{ duration: 0.32, ease: 'easeOut', delay: Math.min(index, 3) * 0.06 }}
        >
          <ResourceCard
            resource={resource}
            type={type}
            features={cardFeatures}
            theme={cardTheme}
          />
        </motion.div>
      ))}
    </div>
  )
} 